import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { Theme } from '../../themes/theme';

export interface SectionHeaderProps {
  label: string;
  count: number | null;
  icon: React.FC<{ className?: string }>;
  collapsed: boolean;
  onToggle: () => void;
  theme: Theme;
  /** Show count even when 0 (e.g., Seleccionados always shows count) */
  showZeroCount?: boolean;
  /** Compact mode — no icon, label starts at px-3 aligned with child items. */
  hideIcon?: boolean;
  /** Optional right-aligned action button (e.g., "+" to create, upload icon) */
  action?: {
    icon: React.FC<{ className?: string }>;
    onClick: () => void;
    title: string;
  };
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  label,
  count,
  icon: Icon,
  collapsed,
  onToggle,
  theme,
  showZeroCount,
  hideIcon,
  action,
}) => {
  // Accent "lights up" when this is a cart-style section (showZeroCount) with items
  const lit = showZeroCount && count != null && count > 0;
  const iconColor = lit ? 'text-nb-accent' : theme.colors.text.secondary;
  const countColor = lit ? 'text-nb-accent' : theme.colors.text.muted;

  const Chevron = collapsed ? ChevronRight : ChevronDown;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={`${collapsed ? 'Expandir' : 'Colapsar'} ${label}`}
      aria-expanded={!collapsed}
      className={`group/hdr flex items-center gap-1.5 px-3 py-2 w-full text-left cursor-pointer select-none hover:${theme.colors.bg.secondary} transition-colors flex-shrink-0`}
    >
      {/* Icon (hidden in compact mode for date groups) */}
      {!hideIcon && <Icon className={`w-3 h-3 flex-shrink-0 ${iconColor}`} />}
      <span className={`text-xs font-medium tracking-wider ${theme.colors.text.secondary}`}>
        {label}
      </span>
      {count != null && (count > 0 || showZeroCount) && (
        <span className={`text-xs ${countColor}`}>({count})</span>
      )}
      {/* Spacer pushes action + chevron to the right */}
      <span className="flex-1" />
      {/* Action button (e.g., upload) — visible on hover, before chevron */}
      {action && (
        <span
          role="button"
          tabIndex={0}
          title={action.title}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
              e.preventDefault();
              action.onClick();
            }
          }}
          className={`p-0.5 -my-0.5 rounded-lg opacity-0 group-hover/hdr:opacity-100 transition-opacity ${theme.colors.state.hover}`}
        >
          <action.icon className={`w-3 h-3 ${theme.colors.text.secondary}`} />
        </span>
      )}
      {/* Chevron — always at the right edge */}
      <Chevron className={`w-3 h-3 flex-shrink-0 ${theme.colors.text.muted}`} />
    </button>
  );
};

export default SectionHeader;
